import { useState } from 'react';
import type { ComparisonGroup } from '@entities/mto';
import { fmtPercent, fmtPercentDelta } from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import {
  DetailCell,
  ExpandButton,
  Muted,
  Notice,
  OfferList,
} from '../styles';
import { AddToNomenclature } from './AddToNomenclature';
import { MinPrice, ExpiredNote } from './MinPrice';
import { OfferRow } from './OfferRow';

/** Строка «похожих позиций»: КП разных поставщиков с одной маркой изделия, без
 *  справочника. Цены сравниваются, выбор поставщика — после добавления в
 *  справочник (выбор хранится по позиции справочника). */
export function GroupRowView({
  group,
  runId,
  canWrite,
  onError,
}: {
  group: ComparisonGroup;
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const names = Array.from(new Set(group.offers.map((o) => o.position_name)));

  return (
    <>
      <tr>
        <td>
          <ExpandButton
            type="button"
            $open={open}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            {group.label}
          </ExpandButton>
        </td>
        <td className="num">{group.offers.length}</td>
        <td className="num">
          <MinPrice
            value={group.min_price_value}
            currency={group.compare_currency}
            offers={group.offers}
          />
        </td>
        <td className="num">{fmtPercent(group.spread_percent)}</td>
        <td className="num">{fmtPercentDelta(group.trend_percent)}</td>
        <td>
          <Badge $tone="neutral">нет в справочнике</Badge>
        </td>
      </tr>
      {open && (
        <tr>
          <DetailCell colSpan={6}>
            <OfferList>
              {group.offers.map((o) => (
                <OfferRow
                  key={o.offer_id}
                  offer={o}
                  group={group.key}
                  compareCurrency={group.compare_currency}
                  selectable={false}
                  checked={false}
                  onPick={() => undefined}
                />
              ))}
            </OfferList>
            {group.incomparable_count > 0 && (
              <Muted>
                Не участвует в сравнении: {group.incomparable_count}. Цены сравниваются внутри одной
                единицы и валюты — курса в системе нет, пересчёт не делаем.
              </Muted>
            )}
            {group.offers.some((o) => o.expired) && <ExpiredNote />}
            {names.length > 1 && (
              <Notice>Написания в КП: {names.map((n) => `«${n}»`).join(', ')}.</Notice>
            )}
            {canWrite && (
              <>
                <Notice>
                  Сведено по марке изделия. Если это одна позиция — добавьте её в справочник, и
                  можно будет выбрать поставщика.
                </Notice>
                <AddToNomenclature
                  offerIds={group.offers.map((o) => o.offer_id)}
                  defaultName={group.label}
                  defaultUnit={group.unit}
                  runId={runId}
                  onError={onError}
                />
              </>
            )}
          </DetailCell>
        </tr>
      )}
    </>
  );
}
